import { type ServiceConfigItemType, ServiceConfigItemTypes, type ServiceConfigsValues } from './constants'
import type { ConfigItem } from './types'

type ConfigValueAdapter = {
  deserialize: (v: unknown) => unknown,
  isEmpty: (v: unknown) => boolean
}

// Keyed by item type with a mandatory `default` fallback; only types that need
// special handling are listed, so a lookup by any ServiceConfigItemType may miss
// and fall through to `default`.
const ConfigValueAdapters: Partial<Record<ServiceConfigItemType, ConfigValueAdapter>> & { default: ConfigValueAdapter } = {
  [ServiceConfigItemTypes.BOOL]: {
    deserialize: value => typeof value === 'string' ? value === 'true' : !!value,
    isEmpty: value => typeof value !== 'boolean',
  },

  default: {
    deserialize: value => value,
    isEmpty: value => !value,
  },
}

/**
 * Resolve the effective config values for a service instance: for each declared item take its value from the
 * single `configs` bag, deserialize it with the type adapter, and fall back to the item's default when empty.
 *
 * ONE bag on purpose. `shared` is a UI directive — it decides who fills a value in and in which dialog — so
 * it has no business steering resolution. A service installed as a custom extension has no shared tier at
 * all: the customer fills in every item in one dialog. Whoever runs a *shared* extension merges the two
 * tiers before calling in; everyone else passes the single bag they already have.
 */
export function prepareServiceConfig(configItems: ConfigItem[], configs: ServiceConfigsValues = {}): ServiceConfigsValues {
  const targetConfig: ServiceConfigsValues = {}

  for (const configItem of configItems) {
    const configName = configItem.name
    const configType = configItem.type

    const adapter = ConfigValueAdapters[configType] || ConfigValueAdapters.default

    targetConfig[configName] = adapter.deserialize(configs[configName])

    if (!adapter.isEmpty(targetConfig[configName])) {
      continue
    }

    const defaultConfig = adapter.deserialize(configItem.defaultValue)

    if (!adapter.isEmpty(defaultConfig)) {
      targetConfig[configName] = defaultConfig
    }
  }

  return targetConfig
}
